/**
 * @file FileUploadZone.js
 * @description Drag-and-drop file upload zone for CSV/XLSX files.
 *
 * Design notes:
 *  - Uses a hidden <input type="file"> as the actual browser file picker so
 *    the custom drag zone UI can be fully styled while still leveraging the
 *    native OS file dialog.
 *  - Drag state is tracked locally so the border / background can animate
 *    to give the user clear visual feedback during a drag-over.
 *  - Validation (type + size) is run immediately on drop or file selection so
 *    errors are surfaced before the parent attempts a parse.
 *  - A "Clear" icon button lets the user remove the selected file and restart.
 *  - After a file is accepted the zone shows a summary chip with the file name
 *    and size in human-readable form.
 */

import React, { useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    IconButton,
    Chip,
    CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import ClearIcon from '@material-ui/icons/Clear';

import { validateFile, ACCEPTED_EXTENSIONS } from '../utils/fileParser';

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
    /**
     * Outer drop zone.  The dashed border, background, and icon colour all
     * animate on hover and drag-over to guide the user.
     */
    dropZone: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 220,
        border: '2px dashed',
        borderColor: ({ isDragging }) =>
            isDragging ? '#43419e' : theme.palette.divider,
        borderRadius: theme.shape.borderRadius * 2,
        backgroundColor: ({ isDragging }) =>
            isDragging ? 'rgba(67, 65, 158, 0.06)' : theme.palette.background.default,
        cursor: 'pointer',
        transition: 'border-color 0.2s ease, background-color 0.2s ease',
        padding: theme.spacing(4),
        '&:hover': {
            borderColor: '#43419e',
            backgroundColor: 'rgba(67, 65, 158, 0.04)',
        },
    },
    uploadIcon: {
        fontSize: 56,
        color: ({ isDragging }) => (isDragging ? '#43419e' : theme.palette.text.secondary),
        marginBottom: theme.spacing(1.5),
        transition: 'color 0.2s ease',
    },
    primaryText: {
        fontWeight: 700,
        color: ({ isDragging }) => (isDragging ? '#43419e' : theme.palette.text.primary),
        transition: 'color 0.2s ease',
        marginBottom: theme.spacing(0.5),
    },
    secondaryText: {
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
    },
    browseLink: {
        color: '#43419e',
        fontWeight: 700,
        cursor: 'pointer',
        textDecoration: 'underline',
        '&:hover': {
            color: '#302f80',
        },
    },
    // ── Selected file state ──────────────────────────────────────────────────
    fileInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    fileIcon: {
        fontSize: 36,
        color: '#43419e',
    },
    fileNameText: {
        fontWeight: 700,
        wordBreak: 'break-all',
        marginBottom: 2,
    },
    fileSizeText: {
        color: theme.palette.text.secondary,
        fontSize: '0.8rem',
    },
    clearButton: {
        color: theme.palette.text.secondary,
        '&:hover': {
            color: theme.palette.error.main,
        },
    },
    rowCountChip: {
        backgroundColor: 'rgba(67, 65, 158, 0.12)',
        color: '#43419e',
        fontWeight: 700,
        fontSize: '0.75rem',
        marginTop: theme.spacing(1),
    },
    errorText: {
        color: theme.palette.error.main,
        fontSize: '0.85rem',
        marginTop: theme.spacing(1),
        textAlign: 'center',
    },
    hiddenInput: {
        display: 'none',
    },
    parsingOverlay: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginTop: theme.spacing(1),
    },
}));

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Format a file size in bytes to a human-readable string.
 *
 * @param {number} bytes
 * @returns {string}  e.g. "2.4 MB"
 */
function formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * FileUploadZone renders a styled drag-and-drop area that accepts .csv and
 * .xlsx files.  It validates the file immediately on selection and calls
 * `onFile` with the accepted File object.
 *
 * @param {object}   props
 * @param {function} props.onFile     Called with the accepted File object
 * @param {File|null} props.file      Currently selected file (controlled)
 * @param {string|null} props.error   Validation or parse error message
 * @param {boolean}  props.isParsing  True while the parent is parsing the file
 * @param {number}   [props.totalRows]  Row count to display as a chip after parsing
 */
function FileUploadZone({ onFile, file, error, isParsing, totalRows }) {
    const [isDragging, setIsDragging] = useState(false);
    // Pass isDragging as a prop to makeStyles so CSS can react to it
    const classes = useStyles({ isDragging });
    const inputRef = useRef(null);

    // ── Drag handlers ─────────────────────────────────────────────────────────

    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        // Only clear the flag when the cursor actually leaves the zone element,
        // not when it moves over a child element
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setIsDragging(false);
        }
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);

            const droppedFile = e.dataTransfer?.files?.[0];
            if (!droppedFile) return;

            const { valid, error: validationError } = validateFile(droppedFile);
            if (valid) {
                onFile(droppedFile);
            } else {
                // Propagate the error upward by calling onFile with null so the
                // parent knows to display it, alongside the error object
                onFile(null, validationError);
            }
        },
        [onFile],
    );

    // ── Click-to-browse handler ───────────────────────────────────────────────

    const handleZoneClick = useCallback(() => {
        if (isParsing) return;
        inputRef.current?.click();
    }, [isParsing]);

    const handleInputChange = useCallback(
        (e) => {
            const selectedFile = e.target.files?.[0];
            if (!selectedFile) return;

            const { valid, error: validationError } = validateFile(selectedFile);
            if (valid) {
                onFile(selectedFile);
            } else {
                onFile(null, validationError);
            }

            // Reset the input value so the same file can be re-selected after
            // a clear action (otherwise the onChange does not fire again)
            e.target.value = '';
        },
        [onFile],
    );

    // ── Clear handler ─────────────────────────────────────────────────────────

    const handleClear = useCallback(
        (e) => {
            e.stopPropagation(); // prevent zone click from re-opening the dialog
            onFile(null, null);
        },
        [onFile],
    );

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <Box>
            <input
                ref={inputRef}
                type="file"
                accept={ACCEPTED_EXTENSIONS.join(',')}
                className={classes.hiddenInput}
                onChange={handleInputChange}
            />

            <Box
                className={classes.dropZone}
                onClick={handleZoneClick}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                role="button"
                tabIndex={0}
                aria-label="File upload zone. Click or drag and drop a CSV or XLSX file."
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleZoneClick();
                    }
                }}
            >
                {/* ── No file selected ──────────────────────────────────────── */}
                {!file && !isParsing && (
                    <>
                        <CloudUploadIcon className={classes.uploadIcon} />
                        <Typography variant="h6" className={classes.primaryText}>
                            {isDragging ? 'Drop your file here' : 'Drag & drop your file here'}
                        </Typography>
                        <Typography variant="body2" className={classes.secondaryText}>
                            or{' '}
                            <span className={classes.browseLink}>browse files</span>
                        </Typography>
                        <Box mt={1.5}>
                            <Typography variant="caption" color="textSecondary">
                                Accepts .csv, .xlsx &nbsp;·&nbsp; Max 50 MB &nbsp;·&nbsp; Up to 10,000 rows
                            </Typography>
                        </Box>
                    </>
                )}

                {/* ── Parsing in progress ───────────────────────────────────── */}
                {isParsing && (
                    <Box className={classes.parsingOverlay}>
                        <CircularProgress size={20} style={{ color: '#43419e' }} />
                        <Typography variant="body2" color="textSecondary">
                            Parsing file…
                        </Typography>
                    </Box>
                )}

                {/* ── File accepted and parsed ──────────────────────────────── */}
                {file && !isParsing && (
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        width="100%"
                    >
                        <Box
                            display="flex"
                            alignItems="flex-start"
                            width="100%"
                            justifyContent="center"
                        >
                            <InsertDriveFileIcon className={classes.fileIcon} />
                            <Box ml={1.5} flex={1} maxWidth={400}>
                                <Typography variant="body1" className={classes.fileNameText}>
                                    {file.name}
                                </Typography>
                                <Typography className={classes.fileSizeText}>
                                    {formatFileSize(file.size)}
                                </Typography>
                                {totalRows > 0 && (
                                    <Chip
                                        label={`${totalRows.toLocaleString()} rows detected`}
                                        size="small"
                                        className={classes.rowCountChip}
                                    />
                                )}
                            </Box>
                            <IconButton
                                size="small"
                                className={classes.clearButton}
                                onClick={handleClear}
                                aria-label="Remove file"
                            >
                                <ClearIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>
                )}
            </Box>

            {/* Error displayed outside the drop zone so it is always readable */}
            {error && (
                <Typography className={classes.errorText} role="alert">
                    {error}
                </Typography>
            )}
        </Box>
    );
}

FileUploadZone.propTypes = {
    /** Called when a file is accepted.  Receives (file: File|null, error: string|null). */
    onFile: PropTypes.func.isRequired,
    /** The currently accepted file, or null when no file is selected. */
    file: PropTypes.instanceOf(File),
    /** A validation or parse error to display below the zone. */
    error: PropTypes.string,
    /** True while the parent component is running parseFile(). */
    isParsing: PropTypes.bool,
    /** Number of data rows in the parsed file (shown as a chip). */
    totalRows: PropTypes.number,
};

FileUploadZone.defaultProps = {
    file: null,
    error: null,
    isParsing: false,
    totalRows: 0,
};

export default FileUploadZone;
